import type {
	OnboardingState,
	OnboardingStep,
	OnboardingStepInput,
} from "@iec-id/shared/contracts/profile";
import { Button, EmptyState, Icon, Spinner } from "@iec-id/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { EcosystemIntro } from "@/components/onboarding/ecosystem-intro";
import { hasSeenIntro } from "@/components/onboarding/intro-storage";
import { OnboardingCard } from "@/components/onboarding/onboarding-card";
import { useOnboardingT } from "@/components/onboarding/onboarding-i18n";
import { returnServiceOf } from "@/components/onboarding/return-service";
import { AboutStep } from "@/components/onboarding/steps/about-step";
import { DocumentsStep } from "@/components/onboarding/steps/documents-step";
import { DoneStep } from "@/components/onboarding/steps/done-step";
import { IdentityStep } from "@/components/onboarding/steps/identity-step";
import { LocaleStep } from "@/components/onboarding/steps/locale-step";
import { NotificationsStep } from "@/components/onboarding/steps/notifications-step";
import { UnderAgeScreen } from "@/components/onboarding/under-age";
import { usePushOffer } from "@/components/onboarding/use-push-offer";
import { parseAuthSearch, resolveSignedInDestination } from "@/lib/auth-flow";
import { clearHawkUser, setHawkUser } from "@/lib/hawk";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/onboarding")({
	validateSearch: parseAuthSearch,
	component: OnboardingScreen,
});

/**
 * Экран мастера. Пять из шести — серверные шаги (`OnboardingStepSchema`),
 * шестой — `notifications` — существует только на клиенте.
 *
 * Почему он не серверный шаг: `onboarding_step` управляет ещё и правом
 * завершить мастер (`isReadyToComplete`). Сделав уведомления обязательным
 * серверным шагом, мы заперли бы в мастере всех, у кого браузер не умеет
 * web-push, — а таких людей мы как раз обязаны пропустить (iOS до 16.4).
 */
type OnboardingScreenKey = OnboardingStep | "notifications";

/** Порядок экранов. Номер в «Шаг N из M» считается по нему. */
const SCREEN_ORDER = [
	"identity",
	"about",
	"locale",
	"documents",
	"notifications",
	"done",
] as const satisfies readonly OnboardingScreenKey[];

/**
 * Экран, с которого мастер открывается в этом заходе.
 *
 * Условия ровно два, и оба обязательны. `imported` — профиль приехал переносом
 * из Портала, то есть подставленное человек вводил сам. `!completed` — мастер
 * ещё не пройден; у пройденного серверный `step` равен `done`, и открывать по
 * нему финальный экран значило бы предложить забрать бонус во второй раз.
 *
 * `done` подменяется на `notifications`, и это не косметика. Серверных шагов
 * пять, экранов шесть: `notifications` живёт только на клиенте и стоит МЕЖДУ
 * последним серверным шагом и финалом. Перенесённый человек, отправивший
 * документы и обновивший страницу, приходит сюда с `onboarding_step = 4`, то
 * есть с серверным `step = "done"` при `completed = null` — и попал бы прямо на
 * экран награды, ни разу не увидев единственный экран, где предлагают
 * уведомления и рассылки. От них зависят приветственные Баллы (см. докблок
 * `submitStep` ниже: спрашивать после экрана с наградой поздно).
 *
 * Всем остальным — первый экран (см. докблок ниже).
 */
function firstScreenOf(state: OnboardingState): OnboardingScreenKey {
	if (!state.imported || state.completed) return "identity";
	return state.step === "done" ? "notifications" : state.step;
}

/**
 * Мастер первичной настройки, шесть экранов (макет 4b + решения владельца
 * 23.08.2026 и 25.08.2026).
 *
 * ── С КАКОГО ЭКРАНА ОТКРЫВАЕТСЯ МАСТЕР ─────────────────────────────────────
 *
 * По умолчанию — с ПЕРВОГО, и это не изменилось. Прежняя редакция этого
 * докблока объясняла почему: автозаполненное ≠ подтверждённое. Имя из Google и
 * ник, выведенный нами из почты, человек не видел ни разу — открыть ему мастер
 * сразу на втором шаге значит записать за него его же имя в экосистеме
 * молчанием.
 *
 * Одно исключение, и оно узкое: `state.imported` — профиль приехал переносом
 * из Портала (`profiles.imported_at`, миграция `0048`). Имя, ник, дату
 * рождения, пол и язык такой человек вводил САМ, своими руками, в нашей же
 * системе; спрашивать их заново — недоверие к собственным данным. Для него
 * экран берётся из серверного `step`, то есть первый НЕЗАПОЛНЕННЫЙ шаг: всё
 * есть → «Документы», нет даты рождения → «О себе», нет ника → «Имя».
 *
 * Что осталось прежним:
 *   · согласие с документами подтверждается лично всегда — переносом шаг
 *     `documents` не засчитывается ни при каких данных;
 *   · `state.completed` перекрывает всё: пройденный мастер повторно не
 *     открывается;
 *   · вернуться назад можно с любого экрана — сервер разрешает повторную
 *     отправку пройденного шага и прогресс не отматывает (`advance` берёт
 *     максимум);
 *   · серверный `onboarding_step` остаётся защитой от перескока
 *     (`isStepAllowed`): экран здесь — это показ, а не право.
 */
function OnboardingScreen() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const t = useOnboardingT();
	const utils = api.useUtils();

	// `null` — человек ещё не переключал экран сам, и показывается тот, с
	// которого мастер открывается (см. `firstScreenOf` и докблок выше).
	// Состояние, а не `useEffect`: серверное состояние приезжает асинхронно, и
	// эффект дал бы кадр с чужим экраном перед подстановкой нужного.
	const [screen, setScreen] = useState<OnboardingScreenKey | null>(null);
	const [rejectedMinAge, setRejectedMinAge] = useState<number | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	/**
	 * Адрес возврата, посчитанный после `completeOnboarding`. Пока он не `null`,
	 * мастер показывает слайды про единый профиль (Д13). У них нет ни номера, ни
	 * полосы прогресса: это уже не шаг мастера, а последнее, что человек видит
	 * перед уходом в сервис.
	 */
	const [afterDone, setAfterDone] = useState<string | null>(null);

	// Настройки push спрашиваем в начале мастера, а не на самом шаге: к нему
	// ответ уже в кэше, и экран не мигает загрузкой.
	const pushOffer = usePushOffer();

	const state = api.profile.onboardingState.useQuery(undefined, { retry: false });
	const me = api.auth.me.useQuery(undefined, { retry: false });

	useEffect(() => {
		if (me.data?.userId) setHawkUser(me.data.userId);
		else clearHawkUser();
	}, [me.data?.userId]);

	// Мастер живёт только за сессией. Её нет — это не ошибка мастера, а вход:
	// возвращаем на форму, сохранив параметры сервиса-инициатора.
	const unauthorized = state.error?.data?.code === "UNAUTHORIZED";
	useEffect(() => {
		if (unauthorized) void navigate({ to: "/", search, replace: true });
	}, [unauthorized, navigate, search]);

	// Мастер пройден — повторно его не открываем. Слайды после финала
	// (`afterDone`) ещё часть этого захода, их не срезаем.
	useEffect(() => {
		if (!state.data?.completed || afterDone !== null) return;
		// Мастер уже пройден, но человек всё ещё внутри входа: если он пришёл из
		// сервиса, увести его надо С КОДОМ, а не «на сайт». Иначе повторный заход
		// в сервис заканчивался витриной IEC ID.
		let cancelled = false;
		void resolveSignedInDestination(search).then((destination) => {
			if (!cancelled) window.location.assign(destination);
		});
		return () => {
			cancelled = true;
		};
	}, [state.data?.completed, afterDone, search]);

	const submitStep = api.profile.submitOnboardingStep.useMutation({
		onSuccess: (result) => {
			setSubmitError(null);
			if (result.state === "rejected") {
				setRejectedMinAge(result.minAge);
				return;
			}
			// Все серверные шаги пройдены — дальше клиентский экран уведомлений,
			// и только за ним финал: от уведомлений зависят приветственные Баллы,
			// и спрашивать о них после экрана с наградой поздно.
			setScreen(result.state === "ready" ? "notifications" : result.step);
			void utils.profile.onboardingState.invalidate();
		},
		onError: () => setSubmitError(t("state.submitFailed")),
	});

	if (rejectedMinAge !== null) {
		return (
			<UnderAgeScreen
				search={search}
				minAge={rejectedMinAge}
				onCorrect={() => {
					setRejectedMinAge(null);
					setScreen("about");
				}}
			/>
		);
	}

	if (state.isPending) {
		return (
			<OnboardingCard search={search}>
				<Loading label={t("state.loadingAria")} />
			</OnboardingCard>
		);
	}

	if (state.isError || !state.data) {
		return (
			<OnboardingCard search={search}>
				<EmptyState
					icon={<Icon name="TriangleAlert" size={32} />}
					title={t("state.errorTitle")}
					description={t("state.errorDescription")}
					action={
						<Button variant="second" onClick={() => void state.refetch()}>
							{t("actions.retry")}
						</Button>
					}
				/>
			</OnboardingCard>
		);
	}

	const data = state.data;

	// Уходим целиком, а не роутером: профиль только что изменился, и полная
	// перезагрузка гарантирует, что ни один кэш запроса его не переживёт.
	const leave = (destination: string) => window.location.assign(destination);

	if (data.completed && afterDone === null) {
		return (
			<OnboardingCard search={search}>
				<Loading label={t("state.loadingAria")} />
			</OnboardingCard>
		);
	}

	const service = returnServiceOf(data, search);

	if (afterDone !== null) {
		const finishLabel = service
			? t("done.goToService", { service: service.name })
			: t("done.openIecId");

		return (
			<EcosystemIntro search={search} finishLabel={finishLabel} onFinish={() => leave(afterDone)} />
		);
	}

	// Экран показа: выбранный человеком либо тот, с которого мастер открылся.
	const current = screen ?? firstScreenOf(data);
	const stepIndex = Math.max(1, SCREEN_ORDER.indexOf(current) + 1);
	const totalSteps = SCREEN_ORDER.length;
	const back = (): void => {
		const previous = SCREEN_ORDER[stepIndex - 2];
		if (previous) setScreen(previous);
	};

	const stepProps = {
		state: data,
		submit: (input: OnboardingStepInput) => {
			setSubmitError(null);
			submitStep.mutate(input);
		},
		pending: submitStep.isPending,
		error: submitError,
		skip: () => setScreen("notifications"),
	};

	const finish = (destination: string): void => {
		// Слайды показываются один раз на браузер: человеку, который уже заводил
		// аккаунт и вернулся сюда по прямой ссылке, читать их второй раз незачем.
		if (hasSeenIntro()) leave(destination);
		else setAfterDone(destination);
	};

	return (
		<OnboardingCard
			search={search}
			stepIndex={stepIndex}
			totalSteps={totalSteps}
			stepLabel={t("progress.step", { current: stepIndex, total: totalSteps })}
			progressLabel={t("progress.aria", { current: stepIndex, total: totalSteps })}
			onBack={stepIndex > 1 ? back : undefined}
			backLabel={t("actions.back")}
		>
			{current === "identity" ? <IdentityStep {...stepProps} /> : null}
			{current === "about" ? <AboutStep {...stepProps} /> : null}
			{current === "locale" ? <LocaleStep {...stepProps} /> : null}
			{current === "documents" ? <DocumentsStep {...stepProps} /> : null}
			{current === "notifications" ? (
				<NotificationsStep
					offer={pushOffer}
					rewardPoints={data.rewardPoints}
					onDone={() => setScreen("done")}
				/>
			) : null}
			{current === "done" ? <DoneStep state={data} search={search} onCompleted={finish} /> : null}
		</OnboardingCard>
	);
}

function Loading({ label }: { label: string }) {
	return (
		<div
			role="status"
			aria-live="polite"
			className="flex min-h-40 flex-col items-center justify-center gap-3 text-fg-muted"
		>
			<Spinner size={24} />
			<span className="t-meta">{label}</span>
		</div>
	);
}
